import Config from "@/config/config";
import { msalInstance } from "@/services/auth/msalConfig";
import { useAdminStore } from "@/stores/admin-store";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export type ErrorDetails = string[] | Record<string, string[]>;

/**
 * Thrown by {@link apiFetch} for non-2xx responses. `message` is the server's
 * message when the body carried one (ResultWrapper `messages[]`, `{ message }`,
 * ProblemDetails `title`/`detail`/`errors`, or a plain-text body), otherwise the
 * legacy "API Error: {status} {statusText}" text — so `(error as Error).message`
 * keeps working for existing callers.
 */
export class ApiError extends Error {
	readonly status: number;
	readonly statusText: string;
	/** Server-supplied messages (validation errors on 400/422), if any. */
	readonly details?: ErrorDetails;
	/** The parsed JSON body, when the response was JSON. */
	readonly body?: unknown;

	constructor(
		message: string,
		status: number,
		statusText: string,
		details?: ErrorDetails,
		body?: unknown,
	) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.statusText = statusText;
		this.details = details;
		this.body = body;
	}
}

interface ParsedErrorBody {
	message?: string;
	details?: ErrorDetails;
	body?: unknown;
}

const isStringArray = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every((item) => typeof item === "string");

function extractErrorDetails(
	body: Record<string, unknown>,
): ErrorDetails | undefined {
	// ResultWrapper responses: { result, status, messages: string[] }
	if (isStringArray(body.messages) && body.messages.length > 0) {
		return body.messages;
	}
	// ProblemDetails: `errors` is either string[] or { field: string[] }
	if (isStringArray(body.errors) && body.errors.length > 0) {
		return body.errors;
	}
	if (body.errors && typeof body.errors === "object") {
		const byField: Record<string, string[]> = {};
		for (const [field, value] of Object.entries(body.errors)) {
			if (isStringArray(value)) byField[field] = value;
			else if (typeof value === "string") byField[field] = [value];
		}
		if (Object.keys(byField).length > 0) return byField;
	}
	return undefined;
}

function extractErrorMessage(
	body: Record<string, unknown>,
	details: ErrorDetails | undefined,
): string | undefined {
	if (details) {
		const flat = Array.isArray(details)
			? details
			: Object.values(details).flat();
		const joined = flat.filter((item) => item.trim()).join("; ");
		if (joined) return joined;
	}
	const candidates = [body.message, body.detail, body.title, body.error];
	for (const candidate of candidates) {
		if (typeof candidate === "string" && candidate.trim()) {
			return candidate.trim();
		}
	}
	return undefined;
}

async function readErrorBody(response: Response): Promise<ParsedErrorBody> {
	let text: string;
	try {
		text = (await response.text()).trim();
	} catch {
		return {};
	}
	if (!text) return {};

	let parsed: unknown;
	try {
		parsed = JSON.parse(text);
	} catch {
		// Plain-text body; skip HTML error pages from proxies/hosting.
		return text.startsWith("<") ? {} : { message: text.slice(0, 500) };
	}
	if (typeof parsed === "string") {
		return { message: parsed.trim() || undefined, body: parsed };
	}
	if (!parsed || typeof parsed !== "object") {
		return { body: parsed };
	}
	const body = parsed as Record<string, unknown>;
	const details = extractErrorDetails(body);
	return {
		message: extractErrorMessage(body, details),
		details,
		body: parsed,
	};
}

/**
 * Base HTTP client that handles auth headers automatically
 */
export async function apiFetch<T>(
	endpoint: string,
	options: RequestInit = {},
): Promise<T> {
	const response = await authorizedFetch(endpoint, options);

	// Handle no-content responses
	if (response.status === 204) {
		return {} as T;
	}

	return response.json();
}

/**
 * Like {@link apiFetch} for a binary body (PDF, spreadsheet): returns the
 * blob plus the file name from Content-Disposition when the browser exposes
 * it. Non-2xx responses throw {@link ApiError} with the server's message.
 */
export async function apiFetchBlob(
	endpoint: string,
	options: RequestInit = {},
): Promise<{ blob: Blob; fileName?: string }> {
	const response = await authorizedFetch(endpoint, options);
	const disposition = response.headers.get("Content-Disposition");
	const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
	return {
		blob: await response.blob(),
		fileName: match?.[1]?.replace(/['"]/g, "") || undefined,
	};
}

/** Adds the bearer token and X-Tenant-ID; throws ApiError on non-2xx. */
async function authorizedFetch(
	endpoint: string,
	options: RequestInit,
): Promise<Response> {
	const account = msalInstance.getActiveAccount();
	if (!account) {
		throw new Error("No active account. Please sign in.");
	}

	// Get token silently
	let accessToken: string;
	try {
		const response = await msalInstance.acquireTokenSilent({
			scopes: (Config.SCOPES || "User.Read").split(" "),
			account: account,
		});
		accessToken = response.accessToken;
	} catch (error) {
		if (error instanceof InteractionRequiredAuthError) {
			// Fallback to interaction if silent fails
			// In a real app, you might trigger a popup or redirect here
			throw new Error("Interaction required for auth token.");
		}
		throw error;
	}

	// Check for selected client context
	const selectedClientId = useAdminStore.getState().selectedClientId;

	const headers: Record<string, string> = {
		Authorization: `Bearer ${accessToken}`,
		...(selectedClientId ? { "X-Tenant-ID": selectedClientId } : {}),
		...(options.headers as Record<string, string>),
	};

	if (!(options.body instanceof FormData)) {
		headers["Content-Type"] = "application/json";
	}

	const baseUrl = Config.API_BASE_URL?.replace(/\/+$/, "") || "";
	const url = `${baseUrl}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;

	const response = await fetch(url, {
		...options,
		headers,
	});

	if (!response.ok) {
		const { message, details, body } = await readErrorBody(response);
		throw new ApiError(
			message || `API Error: ${response.status} ${response.statusText}`,
			response.status,
			response.statusText,
			details,
			body,
		);
	}

	return response;
}

/**
 * Returns the normalized API base URL (no trailing slash). Used by callers that need
 * to construct URLs outside the apiFetch flow — e.g., CSV download anchors.
 */
export function getApiBaseUrl(): string {
	return Config.API_BASE_URL?.replace(/\/+$/, "") || "";
}
